import { fmtBRL, PAY_LABELS } from "./constants";

function esc(v) {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// CSV cell — escape quotes, wrap if it contains separator/newline/quote
function csvCell(v) {
  const s = v === null || v === undefined ? "" : String(v);
  if (/[";,\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function toRows({ openOrders = [], paidOrders = [], openSales = [], paidSales = [] }) {
  const rows = [];
  openOrders.forEach((o) => {
    rows.push({
      status: "Em aberto",
      date: o.exitDate || o.entryDate || "",
      type: "OS",
      ref: o.orderNumber || "",
      desc: `${o.model || ""} — ${(o.services || []).join(", ")}`,
      value: Number(o.total) || 0,
      pay: o.payMethod ? PAY_LABELS[o.payMethod] : "—",
      obs: o.observation || "",
    });
  });
  openSales.forEach((s) => {
    rows.push({
      status: "Em aberto",
      date: s.date || "",
      type: "Peça",
      ref: "",
      desc: s.piece || "",
      value: Number(s.value) || 0,
      pay: s.payMethod ? PAY_LABELS[s.payMethod] : "—",
      obs: s.observation || "",
    });
  });
  paidOrders.forEach((o) => {
    rows.push({
      status: "Pago",
      date: o.exitDate || o.entryDate || "",
      type: "OS",
      ref: o.orderNumber || "",
      desc: `${o.model || ""} — ${(o.services || []).join(", ")}`,
      value: Number(o.total) || 0,
      pay: o.payMethod ? PAY_LABELS[o.payMethod] : "—",
      obs: o.observation || "",
    });
  });
  paidSales.forEach((s) => {
    rows.push({
      status: "Pago",
      date: s.date || "",
      type: "Peça",
      ref: "",
      desc: s.piece || "",
      value: Number(s.value) || 0,
      pay: s.payMethod ? PAY_LABELS[s.payMethod] : "—",
      obs: s.observation || "",
    });
  });
  return rows;
}

function safeFilename(name) {
  return String(name || "cliente")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
}

export function exportClientClosureCSV(client, data) {
  const rows = toRows(data);
  const header = ["Status", "Data", "Tipo", "Nº", "Descrição", "Valor (R$)", "Pagamento", "Observação"];
  const lines = [header.join(";")];
  rows.forEach((r) => {
    lines.push(
      [r.status, r.date, r.type, r.ref, r.desc, fmtBRL(r.value), r.pay, r.obs].map(csvCell).join(";")
    );
  });
  // Summary rows
  lines.push("");
  lines.push(["Total a receber", "", "", "", "", fmtBRL(data.totalOpen), "", ""].map(csvCell).join(";"));
  lines.push(["Total pago", "", "", "", "", fmtBRL(data.totalPaid), "", ""].map(csvCell).join(";"));
  lines.push(
    ["Total geral", "", "", "", "", fmtBRL(data.totalOpen + data.totalPaid), "", ""].map(csvCell).join(";")
  );

  // UTF-8 BOM so Excel opens acentos correctly
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const dt = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `fechamento_${safeFilename(client.name)}_${dt}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function renderRowsHtml(rows, emptyMsg) {
  if (rows.length === 0) {
    return `<tr><td colspan="6" style="padding:14px;text-align:center;color:#94a3b8;font-style:italic">${esc(emptyMsg)}</td></tr>`;
  }
  return rows
    .map(
      (r) => `
      <tr>
        <td>${esc(r.date)}</td>
        <td><span class="tag">${esc(r.type)}</span>${r.ref ? ` <span class="ref">${esc(r.ref)}</span>` : ""}</td>
        <td>${esc(r.desc)}</td>
        <td>${esc(r.pay)}</td>
        <td style="text-align:right;font-variant-numeric:tabular-nums;font-weight:600">R$ ${fmtBRL(r.value)}</td>
      </tr>`
    )
    .join("");
}

export function printClientClosure(client, data) {
  const win = window.open("", "_blank", "width=860,height=1000");
  if (!win) return;

  const all = toRows(data);
  const openRows = all.filter((r) => r.status === "Em aberto");
  const paidRows = all.filter((r) => r.status === "Pago");

  const logoUrl = `${window.location.origin}/logo-sm.png`;
  const now = new Date();
  const emittedAt = now.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Fechamento — ${esc(client.name)}</title>
        <style>
          * { box-sizing: border-box; }
          body {
            font-family: -apple-system, "Segoe UI", "Helvetica Neue", sans-serif;
            color: #0f172a; margin: 0; padding: 28px;
          }
          .head {
            display: flex; justify-content: space-between; align-items: center;
            border-bottom: 3px solid #1e3a8a; padding-bottom: 14px; margin-bottom: 22px;
          }
          .head img { height: 56px; width: auto; }
          .meta { text-align: right; font-size: 11px; color: #475569; line-height: 1.4; }
          .meta b { color: #1e3a8a; font-size: 15px; display: block; margin-bottom: 2px; }
          .client-box {
            border: 1px solid #e2e8f0; border-radius: 10px;
            padding: 12px 16px; background: #f8fafc; margin-bottom: 16px;
          }
          .client-box .label {
            font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.08em;
          }
          .client-box .name { font-size: 20px; font-weight: 800; color: #0f172a; margin-top: 2px; }
          .client-box .phone { font-size: 13px; color: #475569; }

          .kpis { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; margin-bottom: 22px; }
          .kpi {
            border-radius: 10px; padding: 10px 14px;
            border: 1px solid transparent;
          }
          .kpi .lbl { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700; }
          .kpi .val { font-size: 22px; font-weight: 800; margin-top: 2px; font-variant-numeric: tabular-nums; }
          .kpi.receber { background: #fef2f2; border-color: #fecaca; color: #991b1b; }
          .kpi.pago    { background: #ecfdf5; border-color: #a7f3d0; color: #065f46; }
          .kpi.total   { background: #eff6ff; border-color: #bfdbfe; color: #1e40af; }

          h3 {
            margin: 22px 0 8px; font-size: 12px; color: #1e3a8a;
            letter-spacing: 0.1em; text-transform: uppercase;
            padding-bottom: 4px; border-bottom: 2px solid #e2e8f0;
          }
          table { width: 100%; border-collapse: collapse; font-size: 12.5px; }
          th {
            text-align: left; background: #1e3a8a; color: #fff;
            padding: 8px 10px; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.05em;
          }
          td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
          tbody tr:nth-child(even) { background: #f8fafc; }
          .tag {
            display: inline-block; padding: 1px 6px; border-radius: 6px;
            background: #e2e8f0; color: #475569; font-size: 10px; font-weight: 700; text-transform: uppercase;
          }
          .ref { color: #94a3b8; font-size: 10px; margin-left: 4px; font-weight: 600; }

          .foot {
            margin-top: 34px; font-size: 11px; color: #64748b;
            border-top: 1px dashed #cbd5e1; padding-top: 12px;
          }
          .sign { margin-top: 44px; display: flex; justify-content: space-around; font-size: 11px; color: #64748b; }
          .sign div { text-align: center; }
          .sign span { display: block; border-top: 1px solid #94a3b8; padding-top: 4px; width: 220px; margin-top: 32px; }

          @media print {
            body { padding: 12mm; }
            .kpis { break-inside: avoid; }
            h3, table { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="head">
          <img src="${esc(logoUrl)}" alt="Phone Lab" />
          <div class="meta">
            <b>Fechamento de cliente</b>
            Emitido em ${esc(emittedAt)}
          </div>
        </div>

        <div class="client-box">
          <div class="label">Cliente</div>
          <div class="name">${esc(client.name)}</div>
          <div class="phone">${esc(client.phone || "")}</div>
        </div>

        <div class="kpis">
          <div class="kpi receber">
            <div class="lbl">A receber</div>
            <div class="val">R$ ${fmtBRL(data.totalOpen)}</div>
          </div>
          <div class="kpi pago">
            <div class="lbl">Já pago</div>
            <div class="val">R$ ${fmtBRL(data.totalPaid)}</div>
          </div>
          <div class="kpi total">
            <div class="lbl">Total geral</div>
            <div class="val">R$ ${fmtBRL(data.totalOpen + data.totalPaid)}</div>
          </div>
        </div>

        <h3>Itens em aberto</h3>
        <table>
          <thead>
            <tr>
              <th style="width:80px">Data</th>
              <th style="width:120px">Tipo</th>
              <th>Descrição</th>
              <th style="width:100px">Pagamento</th>
              <th style="width:100px;text-align:right">Valor</th>
            </tr>
          </thead>
          <tbody>${renderRowsHtml(openRows, "Nenhum item em aberto — cliente quitado.")}</tbody>
        </table>

        <h3>Histórico pago</h3>
        <table>
          <thead>
            <tr>
              <th style="width:80px">Data</th>
              <th style="width:120px">Tipo</th>
              <th>Descrição</th>
              <th style="width:100px">Pagamento</th>
              <th style="width:100px;text-align:right">Valor</th>
            </tr>
          </thead>
          <tbody>${renderRowsHtml(paidRows, "Nenhum pagamento registrado.")}</tbody>
        </table>

        <div class="foot">
          Documento gerado pelo sistema de gestão Phone Lab · válido apenas como demonstrativo interno.
        </div>

        <div class="sign">
          <div><span>Cliente</span></div>
          <div><span>Assistência Técnica</span></div>
        </div>
      </body>
    </html>
  `;

  win.document.open();
  win.document.write(html);
  win.document.close();
  win.onload = () => {
    win.focus();
    win.print();
  };
}
